// Fichier : AddUser.tsx
// Ce fichier contient la définition et l'implémentation du composant pour ajouter un nouvel utilisateur.
// Il utilise React Hook Form et Zod pour la validation de formulaire, RTK Query Pour la gestion des mutations , et React Roastify pour Notifications .

import React from "react";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";

import { useCreateUserMutation } from "@/state/usersApiSlice";
import { useNavigate } from "react-router-dom";
import { IoReturnDownBackSharp } from "react-icons/io5";
import { userSchema } from "../../zod Schemas/zodUserSchema";
import styles from "./AddUser.module.css";
import { toast } from "react-toastify";
import { Checkbox } from "@/components/ui/checkbox";
import SkeletonUI from "./SkeletonUI";

// Définition du type de données du formulaire basé sur le schéma Zod
type UserFormData = z.infer<typeof userSchema>;

// Fonction principale du composant AddUser
const AddUser: React.FC = () => {
  // Fonction de notification pour afficher un message de succès
  const notifyAddSuccess = () =>
    toast.success("User Added Successfully", {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
    });

  const navigate = useNavigate(); // Hook pour la navigation Donné par React Router
  const [createUser ,{isLoading} ] = useCreateUserMutation(); // Mutation pour créer un nouvel utilisateur , elle Retourne la Fonction CreateUser pour l'utiliser par la suite

  // Hook pour gérer le formulaire avec validation Zod
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
  } = useForm<UserFormData>({
    resolver: zodResolver(userSchema), // Utilisation de Zod pour la validation du formulaire
    defaultValues: {
      username: "",
      password: "",
      tenant_id: "",
      wordpress_domain: "",
      chatwoot_domain: "",
      wordpress_user: "",
      wordpress_password: "",
      chatwoot_user: "",
      chatwoot_password: "",
      printing_capabilities: false,
      cashdrawer_capabilities: false,
      second_screen_capabilities: false,
      avoir_decaisser_capabilities: false,
    }, // Valeurs par défaut du formulaire
  });

  // Fonction appelée lors de la soumission du formulaire
  const onSubmit: SubmitHandler<UserFormData> = async (data) => {
    // Préparer les données de l'utilisateur à créer
    const userToCreate = {
      ...data,
      tenant_id: parseInt(data.tenant_id, 10),
    }; // la on a fait un parseInt pour le 'tenant_id' parce que il doit etre number pour le soummetre , alors que le text input rend toujours des string .
    try {
      const createdUser = await createUser(userToCreate).unwrap();
      if (createdUser) {
        // Si la création réussit, afficher une notification et rediriger pour voir l'utilisateur créé
        notifyAddSuccess();
        navigate(`/users/${createdUser.id}/view`);
      }
      reset(); // Réinitialiser le formulaire , c'est là où on a besoin de valeurs par defaults de React hook Form , la fonction reset rénitialise le formulaire par ces valeurs.
    } catch (error) {
      console.error("Failed to create user:", error);
      alert("Failed to create user. Please try again.");
    }
  };

  // Retourne l'interface utilisateur du composant
  return isLoading ?   (<SkeletonUI />) :(
    <div className={styles.container}>
      <div className={styles.content}>
        <h2 className={styles.title}>Add User</h2>
        <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
          {/* Groupe de champs de formulaire pour le nom d'utilisateur */}
          <div className={styles.formGroup}>
            <label className={styles.label}>Username:</label>
            <input {...register("username")} className={styles.input} />
            {errors.username && (
              <p className={styles.errorMessage}>{errors.username.message}</p>
            )}
          </div>

          {/* Groupe de champs de formulaire pour le mot de passe */}
          <div className={styles.formGroup}>
            <label className={styles.label}>Password:</label>
            <input
              type="password"
              {...register("password")}
              className={styles.input}
            />
            {errors.password && (
              <p className={styles.errorMessage}>{errors.password.message}</p>
            )}
          </div>

          {/* Groupe de champs de formulaire pour le rôle */}
          {/* <div className={styles.formGroup}>
            <label className={styles.label}>Role:</label>
            <select
              id="role"
              {...register("role")}
              name="role"
              required
              className={`${styles.input} ${styles.inputDisabled}`}
            >
              <option value="USER">User</option>
              <option value="ADMIN">Admin</option>
            </select>
            {errors.role && (
              <p className={styles.errorMessage}>{errors.role.message}</p>
            )}
          </div> */}

          {/* Groupe de champs de formulaire pour l'ID du locataire */}
          <div className={styles.formGroup}>
            <label className={styles.label}>Tenant ID:</label>
            <input {...register("tenant_id")} className={styles.input} />
            {errors.tenant_id && (
              <p className={styles.errorMessage}>{errors.tenant_id.message}</p>
            )}
          </div>

          {/* Groupe de champs de formulaire pour le domaine WordPress */}
          <div className={styles.formGroup}>
            <label className={styles.label}>WordPress Domain:</label>
            <input {...register("wordpress_domain")} className={styles.input} />
            {errors.wordpress_domain && (
              <p className={styles.errorMessage}>
                {errors.wordpress_domain.message}
              </p>
            )}
          </div>


          {/* Groupe de champs de formulaire pour l'utilisateur WordPress */}
          <div className={styles.formGroup}>
            <label className={styles.label}>WordPress User:</label>
            <input {...register("wordpress_user")} className={styles.input} />
            {errors.wordpress_user && (
              <p className={styles.errorMessage}>
                {errors.wordpress_user.message}
              </p>
            )}
          </div>

          {/* Groupe de champs de formulaire pour le mot de passe WordPress */}
          <div className={styles.formGroup}>
            <label className={styles.label}>WordPress Password:</label>
            <input
              type="password"
              {...register("wordpress_password")}
              className={styles.input}
            />
            {errors.wordpress_password && (
              <p className={styles.errorMessage}>
                {errors.wordpress_password.message}
              </p>
            )}
          </div>


          {/* Groupe de champs de formulaire pour le domaine Chatwoot */}
          <div className={styles.formGroup}>
            <label className={styles.label}>Chatwoot Domain:</label>
            <input {...register("chatwoot_domain")} className={styles.input} />
            {errors.chatwoot_domain && (
              <p className={styles.errorMessage}>
                {errors.chatwoot_domain.message}
              </p>
            )}
          </div>


          {/* Groupe de champs de formulaire pour l'utilisateur Chatwoot */}
          <div className={styles.formGroup}>
            <label className={styles.label}>Chatwoot User:</label>
            <input {...register("chatwoot_user")} className={styles.input} />
            {errors.chatwoot_user && (
              <p className={styles.errorMessage}>
                {errors.chatwoot_user.message}
              </p>
            )}
          </div>

          {/* Groupe de champs de formulaire pour le mot de passe Chatwoot */}
          <div className={styles.formGroup}>
            <label className={styles.label}>Chatwoot Password:</label>
            <input
              type="password"
              {...register("chatwoot_password")}
              className={styles.input}
            />
            {errors.chatwoot_password && (
              <p className={styles.errorMessage}>
                {errors.chatwoot_password.message}
              </p>
            )}
          </div>

  
          {/* Groupe de cases à cocher pour les capacités */}
          <div className={styles.checkboxContainer}>
            <div className={styles.checkboxGroup}>
              <label className={styles.label}>Printing Capabilities:</label>
              <Controller
                name="printing_capabilities"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    className="h-6 w-6"
                  />
                )}
              />
            </div>

            <div className={styles.checkboxGroup}>
              <label className={styles.label}>Cashdrawer Capabilities:</label>
              <Controller
                name="cashdrawer_capabilities"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    className="h-6 w-6"
                  />
                )}
              />
            </div>

            <div className={styles.checkboxGroup}>
              <label className={styles.label}>
                Second Screen Capabilities:
              </label>
              <Controller
                name="second_screen_capabilities"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    className="h-6 w-6"
                  />
                )}
              />
            </div>

            <div className={styles.checkboxGroup}>
              <label className={styles.label}>
                Delete Transactions Capabilities:
              </label>

              <Controller
                name="avoir_decaisser_capabilities"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    className="h-6 w-6"
                  />
                )}
              />
            </div>
          </div>

          <div className={styles.flex}>
            <button type="submit" className={styles.button}>
              Add User
            </button>
            <button
              type="button"
              onClick={() => navigate("/users/all")}
              className={styles.backButton}
            >
              Back to Users List <IoReturnDownBackSharp />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddUser;
